import type { AlgoliaCatalogItem, CatalogItemResponse } from '~/types/api'
import { isAlgoliaCatalogItem, isCatalogItemBeer } from '~/types/api'

type DrinkCreationEvents =
  | 'create_drink_opened'
  | 'parent_style_selected'
  | 'sub_style_selected'
  | 'producer_chosen'
  | 'create_drink_submitted'
  | 'create_drink_succeeded'
  | 'create_drink_failed'

export type CleverTapEvent =
  | 'Charged' // https://developer.clevertap.com/docs/events#what-makes-charged-a-special-event
  | 'onboarding_complete'
  | 'checkin'
  | 'venue_subscribe'
  | 'view_shop_screen'
  | 'view_product_details'
  | 'view_settings'
  | 'add_to_cart'
  | 'remove_from_cart'
  | 'clear_cart'
  | 'view_cart'
  | 'begin_checkout'
  | 'search'
  | 'share_product'
  | 'discover_shop_tap'
  | 'checkout_summary_loaded'
  | 'checkout_summary_error'
  | 'checkout_add_payment_info'
  | 'checkout_pay_now_press'
  | 'advocacy_link_tap'
  | 'insider_purchase'
  | 'shop_web_page_view'
  | 'shop_web_age_verification'
  | 'shop_web_zip_entered_success'
  | 'shop_web_zip_entered_failure'
  | 'shop_web_continue_to_payment_click'
  | 'shop_web_delivery_address_added'
  | 'oznr_pickup_list_view'
  | 'oznr_merchant_tap'
  | 'shop_web_state_entered_success'
  | 'brewery_portal_flag_clicked'
  | 'brewery_portal_flag_submitted'
  | 'brewery_portal_upload_started'
  | 'brewery_portal_upload_success'
  | 'brewery_portal_upload_failed'
  | 'brewery_portal_match_success'
  | 'brewery_portal_match_failed'
  | 'brewery_portal_manual_reassignment'
  | 'brewery_portal_confirm_match'
  | 'brewery_portal_item_match_confirmed_clicked'
  | 'brewery_portal_item_beer_external_clicked'
  | 'brewery_portal_item_collapsed_toggled'
  | 'brewery_portal_item_image_change_requested'
  | 'brewery_portal_upload_guidelines_viewed'
  | 'brewery_portal_viewed'
  | 'brewery_portal_tab_clicked'
  | 'brewery_portal_item_clicked'
  | 'beverage_preference_toggled'
  | DrinkCreationEvents

export type CleverTapUserProfileKey =
  | 'brewery_portal_needs_action_count'
  | 'current_cart_skus'
  | 'current_catalog_selection'
  | 'current_shop_state'
  | 'category-unsubscribe' // Reserved key: https://developer.clevertap.com/docs/upload-user-profiles-api
  | 'category-resubscribe' // Reserved key: https://developer.clevertap.com/docs/upload-user-profiles-api
  | 'Email'
  | 'Country'
  | 'Region'
  | 'is_anonymous'
  | 'Language'
  | 'last_checkin_beer'
  | 'last_checkin_category'
  | 'oznr_merchant_count'
  | 'personalized_box_purchased'
  | 'push_enabled_back_in_stock'
  | 'push_enabled_beer_offers'
  | 'push_enabled_merch_offers'
  | 'push_enabled_product_announcements'
  | 'push_enabled_wishlist_beers'
  | 'push_enabled_year_in_beer'
  | 'shippable_for_beer'
  | 'shop_notify_me_state'
  | 'subscription_status'

// Copied from node_modules/@types/clevertap-web-sdk/clevertap.d.ts
interface SiteData {
  Name?: string
  Identity?: string | number
  Gender?: 'M' | 'F'
  Employed?: 'Y' | 'N'
  Married?: 'Y' | 'N'
  Education?: 'School' | 'College' | 'Graduate'
  Age?: string | number
  DOB?: string | number | Date
  Phone?: string | number
}

export type CleverTapUserProfileKeyValues = SiteData &
  Partial<Record<CleverTapUserProfileKey, unknown>>

// CleverTap does not allow SKUs with periods when we upload our catalog to them, so
// we replace with underscores. Therefore in order to match CleverTap analytics events to our
// catalog we need to make sure the app is santizing SKUs in the same way.
export const cleverTapSanitizeSku = (sku: string) => {
  return sku.replaceAll('.', '_')
}

const getBeerMetadata = (
  catalogItem: CatalogItemResponse | AlgoliaCatalogItem
): Record<string, unknown> => {
  if (isAlgoliaCatalogItem(catalogItem)) {
    if (catalogItem.type === 'Beer') {
      return {
        beerId: catalogItem.untappd_id,
        beerName: catalogItem.beer_name,
        breweryName: catalogItem.brewery_name,
        beerStyle: catalogItem.style,
      }
    }
  } else {
    if (isCatalogItemBeer(catalogItem)) {
      return {
        beerId: catalogItem.item_details.beer?.untappd_id,
        beerName: catalogItem.item_details.beer?.name,
        breweryName: catalogItem.item_details.beer?.brewery_name,
        beerStyle: catalogItem.item_details.beer?.style,
      }
    }
  }
  return {}
}

export const getCleverTapCatalogItemParams = (
  catalogItem: CatalogItemResponse | AlgoliaCatalogItem
): Record<string, unknown> => {
  const beerMetadata = getBeerMetadata(catalogItem)

  if (isAlgoliaCatalogItem(catalogItem)) {
    return {
      catalogItemId: catalogItem.objectID,
      itemType: catalogItem.type,
      ...beerMetadata,
    }
  } else {
    return {
      catalogItemId: catalogItem.id,
      itemType: catalogItem.type.replace('CatalogItem::', ''),
      ...beerMetadata,
    }
  }
}
